import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { IonicModule, PopoverController, LoadingController, NavController, ModalController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { EditAccountComponent } from 'src/app/accounts/edit-account/edit-account.component';
import { PRIVATE_CHANNEL_NAME, routes } from 'src/app/constants';
import { formatLink, generateCustomFieldsArray } from 'src/app/utils/utils';
import { Conversation, Profile } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { UsersService } from 'src/services/users.service';

import { ConversationLinksListComponent } from '../../conversation-details/conversation-links-list/conversation-links-list.component';
import { ActionDropdownComponent } from '../action-dropdown/action-dropdown.component';
import { AvatarModule } from '../avatar/avatar.module';
import { ConversationListComponent } from '../conversation-list/conversation-list.component';
import { ResponseInputModule } from '../custom-fields/response-input/response-input.module';

@Component({
  selector: 'app-conversation-info',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    AvatarModule,
    NgxTippyModule,
    SharedPipesModule,
    ConversationListComponent,
    ConversationLinksListComponent,
    ActionDropdownComponent,
    ResponseInputModule,
    EditAccountComponent,
  ],
  template: `
    <ion-accordion-group multiple="true" style="margin-top: 5px" (ionChange)="accordionGroupChange($event)">
      <!-- Submitter Info Section -->
      <ion-accordion value="info" [ngClass]="{ 'border-bottom': isInfoExpanded }">
        <ion-item slot="header" style="--border-color: transparent;">
          <ion-label style="display: flex; align-items: center">
            <div class="image-container">
              <div class="main-image">
                <img
                  *ngIf="conversation?.submitterUser?.photo"
                  [src]="conversation.submitterUser.photo"
                  class="profile-image"
                />
                <ion-icon *ngIf="!conversation?.submitterUser?.photo" name="person" class="fallback-icon"></ion-icon>
              </div>
              <div class="source-badge" [style.background]="getSourceColor()" [hidden]="isInfoExpanded">
                <ion-icon [name]="getSourceIcon()" style="color: white"></ion-icon>
              </div>
            </div>
            <div class="title-container">
              <h2>{{ conversation?.submitterUser?.name || 'Unknown Submitter' }}</h2>
              <p class="subtitle" [hidden]="isInfoExpanded">
                <span *ngIf="conversation.customer">{{ conversation.customer.name ?? PRIVATE_CHANNEL_NAME }}</span>
                <span
                  *ngIf="
                    conversation.initialMessage?.sourceType !== 'slack' &&
                    conversation.initialMessage?.sourceType !== 'slack-bot-dm'
                  "
                >
                  via {{ conversation.initialMessage?.sourceType }}</span
                >
              </p>
            </div>
            <div class="account-image-container" *ngIf="conversation?.account?.imageUrl && !isInfoExpanded">
              <img
                [src]="conversation.account.imageUrl"
                class="account-image"
                [ngxTippy]="conversation.account.name"
                [tippyProps]="{
                  touch: false,
                  arrow: false,
                  placement: 'left'
                }"
              />
            </div>
          </ion-label>
        </ion-item>
        <div class="details-content" slot="content">
          <ion-list class="transparent-bg no-padding" lines="none">
            <ion-item class="align-top" *ngIf="conversation?.submitterUser?.email">
              <ion-label slot="start">
                <p>Email</p>
              </ion-label>
              <ion-chip
                mode="ios"
                class="select-chip select-outline"
                [outline]="true"
                ngxTippy
                [tippyProps]="{
                  touch: false,
                  arrow: false,
                  placement: 'bottom',
                  content: 'Click to copy'
                }"
                (click)="copyText(conversation.submitterUser.email)"
              >
                <ion-label>{{ conversation.submitterUser.email }}</ion-label>
              </ion-chip>
            </ion-item>
            <ion-item class="align-top" *ngFor="let field of customFields">
              <ion-label slot="start">
                <p>{{ field.label }}</p>
              </ion-label>
              <ion-chip
                mode="ios"
                class="select-chip select-outline"
                [outline]="true"
                ngxTippy
                [tippyProps]="{
                  touch: false,
                  arrow: false,
                  placement: 'bottom',
                  content: 'Click to copy'
                }"
                (click)="copyText(field.value)"
              >
                <ion-label>{{ field.value }}</ion-label>
              </ion-chip>
            </ion-item>
            <div class="button-container">
              <ion-button
                expand="block"
                color="light"
                class="btn-small view-profile-btn"
                (click)="viewProfile()"
                [hidden]="!conversation?.submitterUser?.id"
              >
                View Profile
              </ion-button>
              <ion-button color="dark" fill="clear" class="btn-small overflow-btn" (click)="showOverflowMenu($event)">
                <ion-icon name="ellipsis-vertical"></ion-icon>
              </ion-button>
            </div>
          </ion-list>
        </div>
      </ion-accordion>

      <!-- Channel/Customer Details Section -->
      <ion-accordion value="channel" [hidden]="!isInfoExpanded" [ngClass]="{ 'border-bottom': isInfoExpanded }">
        <ion-item slot="header">
          <ion-label style="display: flex; align-items: center; font-weight: 500">
            <div
              [style.background]="getSourceColor()"
              style="width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-right: 12px;"
            >
              <ion-icon [name]="getSourceIcon()" style="color: white; font-size: 20px;"></ion-icon>
            </div>
            {{ conversation.customer?.name || 'Channel Details' }}
            <span *ngIf="conversation.initialMessage?.sourceType === 'slack-bot-dm'" style="margin-left: 4px">
              (via Bot DM)
            </span>
          </ion-label>
        </ion-item>
        <div class="details-content" slot="content">
          <ion-list class="transparent-bg no-padding" lines="none">
            <ion-item class="align-top" *ngIf="conversation?.customer">
              <ion-label slot="start">
                <p>Channel</p>
              </ion-label>
              <ion-chip
                mode="ios"
                class="select-chip select-outline"
                [outline]="true"
                (click)="viewCustomer(conversation.customerId)"
                ngxTippy
                [tippyProps]="{
                  touch: false,
                  arrow: false,
                  placement: 'bottom',
                  content: 'View channel details'
                }"
              >
                <ion-label>{{ conversation.customer.name ?? PRIVATE_CHANNEL_NAME }}</ion-label>
                <ion-icon name="open-outline" style="font-size: 0.9rem; margin-left: 4px"></ion-icon>
              </ion-chip>
            </ion-item>
            <ion-item class="align-top" *ngIf="conversation?.project?.name">
              <ion-label slot="start">
                <p>Project</p>
              </ion-label>
              <ion-chip mode="ios" class="select-chip select-outline" [outline]="true" [disabled]="true">
                <ion-label>{{ conversation.project.name }}</ion-label>
              </ion-chip>
            </ion-item>
            <ion-item class="align-top" *ngIf="conversation.customer?.tags?.length">
              <ion-label slot="start">
                <p>Channel tags</p>
              </ion-label>
              <ion-label text-wrap>
                <ion-chip
                  mode="ios"
                  color="primary"
                  class="chip tag-chip"
                  *ngFor="let tag of conversation.customer?.tags"
                >
                  <ion-label>{{ tag.name }}</ion-label>
                </ion-chip>
              </ion-label>
            </ion-item>
          </ion-list>
        </div>
      </ion-accordion>

      <!-- Account Details Section -->
      <ion-accordion
        value="account"
        expanded="true"
        *ngIf="conversation?.account"
        [hidden]="!isInfoExpanded"
        [ngClass]="{ 'border-bottom': isInfoExpanded }"
      >
        <ion-item slot="header">
          <ion-label style="display: flex; align-items: center; font-weight: 500">
            <img
              *ngIf="conversation?.account?.imageUrl"
              [src]="conversation.account.imageUrl"
              style="
                object-fit: cover;
                border-radius: 10px;
                width: 36px;
                height: 36px;
                margin-right: 12px;
                border: 1px solid var(--ion-subtle-border-color);
              "
            />
            {{ conversation.account.name }}
          </ion-label>
        </ion-item>
        <div class="details-content" slot="content">
          <ion-list class="transparent-bg no-padding" lines="none">
            <ion-item class="align-top" *ngIf="conversation?.account?.tags?.length">
              <ion-label slot="start">
                <p>Account tags</p>
              </ion-label>
              <ion-label text-wrap>
                <ion-chip
                  mode="ios"
                  color="primary"
                  class="chip tag-chip"
                  *ngFor="let tag of conversation.account?.tags"
                >
                  <ion-label>{{ tag.name }}</ion-label>
                </ion-chip>
                <i *ngIf="!conversation.account?.tags?.length">None</i>
              </ion-label>
            </ion-item>
          </ion-list>
          <app-response-input
            [responseInputFields]="conversation.account?.customFields ?? []"
            [isReadOnly]="true"
            layout="list"
          >
          </app-response-input>
          <ion-button
            class="btn-small"
            color="light"
            size="block"
            (click)="editAccount(conversation.accountId)"
            style="margin: 5px 15px 15px 15px"
            *ngIf="authService.isTenantAdmin"
          >
            View Account
            <ion-icon name="open-outline" class="icon-padded-left"></ion-icon>
          </ion-button>
        </div>
      </ion-accordion>
    </ion-accordion-group>
  `,
  styles: [
    `
      .align-top {
        align-items: start;
        --min-height: 36px;
      }
      .select-chip {
        margin: 0;
        height: 34px;
        font-size: 0.8rem;
        border: 1px solid transparent;
        box-shadow: none;
        &:hover {
          background: transparent;
          border: 1px solid var(--input-border-color);
          .chevron-icon {
            display: inline-block;
          }
        }
      }
      .chevron-icon {
        display: none;
      }
      .step-content {
        border-bottom: 1px solid var(--ion-subtle-border-color);
      }
      ion-label[slot='start'] {
        margin: 7px 0;
        flex: none;
        width: 33%;
      }
      :host ::ng-deep {
        ion-accordion {
          background: transparent;
          ion-item {
            --border-color: transparent;
          }
        }
      }
      .image-container {
        position: relative;
        width: 36px;
        height: 36px;
        margin-right: 12px;
      }
      .main-image {
        width: 100%;
        height: 100%;
        border-radius: 99px;
        background: var(--ion-color-step-50);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }
      .profile-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 99px;
        border: 1px solid var(--ion-subtle-border-color);
      }
      .fallback-icon {
        font-size: 24px;
        color: var(--ion-color-step-600);
        padding: 10px;
        background: var(--ion-color-primary);
        border-radius: 99px;
        color: var(--ion-color-step-0);
      }
      .source-badge {
        position: absolute;
        bottom: 1px;
        right: -8px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid var(--ion-background-color);
        z-index: 1;
        ion-icon {
          font-size: 12px;
        }
      }
      .title-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
      }
      h2 {
        margin: 0;
        font-size: 0.8rem;
        font-weight: 500;
        color: var(--ion-text-color);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .subtitle {
        margin: 0;
        font-size: 0.75rem;
        color: var(--ion-color-step-600);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .button-container {
        display: flex;
        gap: 8px;
        padding: 8px 16px 16px;
      }
      .view-profile-btn {
        flex: 1;
        margin: 0;
      }
      .overflow-btn {
        width: 30px;
        margin: 0;
        --padding-start: 0;
        --padding-end: 0;
      }
      ion-accordion {
        background: transparent;
      }
      .account-image-container {
        margin-left: auto;
        width: 36px;
        height: 36px;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid var(--ion-subtle-border-color);
      }
      .account-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .border-bottom {
        border-bottom: 1px solid var(--ion-subtle-border-color);
      }
    `,
  ],
})
export class ConversationInfoComponent {
  @Input() conversation: Conversation;
  customFields: any[] = [];
  isInfoExpanded = false;

  PRIVATE_CHANNEL_NAME = PRIVATE_CHANNEL_NAME;

  constructor(
    private messageService: MessageService,
    private popoverCtrl: PopoverController,
    public authService: AuthService,
    private searchablePopoverService: SearchablePopoverService,
    private conversationsService: ConversationsService,
    private loadingCtrl: LoadingController,
    private navController: NavController,
    private modalCtrl: ModalController,
    private usersService: UsersService,
  ) {}

  getSourceIcon(): string {
    switch (this.conversation?.initialMessage?.sourceType) {
      case 'slack':
        return 'logo-slack';
      case 'email':
        return 'mail';
      case 'teams':
        return 'custom-teams';
      case 'widget':
        return 'chatbubbles';
      case 'dashboard':
        return 'apps';
      case 'slack-bot-dm':
        return 'custom-bot';
      case 'automation':
        return 'cog';
      default:
        return 'chatbubbles';
    }
  }

  getSourceColor(): string {
    switch (this.conversation?.initialMessage?.sourceType) {
      case 'slack':
        return '#4A154B'; // Slack brand color
      case 'email':
        return '#4285F4'; // Blue
      case 'teams':
        return '#464EB8'; // Teams brand color
      case 'widget':
        return '#2dd36f'; // Success color
      case 'dashboard':
        return '#ffc409'; // Warning color
      case 'automation':
        return '#6c3483'; // Purple
      default:
        return 'var(--ion-color-medium)';
    }
  }

  async showTagsPopover(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: 'TagsPopoverComponent', // You'll need to create/import this
      componentProps: {
        conversationId: this.conversation.id,
        existingTags: this.conversation.tags,
      },
      event,
      translucent: true,
    });
    await popover.present();
  }

  async showLinkConversationModal() {
    const modal = await this.popoverCtrl.create({
      component: 'LinkConversationModalComponent', // You'll need to create/import this
      componentProps: {
        conversationId: this.conversation.id,
      },
    });
    await modal.present();
  }

  copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  async showOverflowMenu(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options: [
          {
            label: 'Change Submitter',
            icon: 'swap-horizontal-outline',
            handler: () => this.changeSubmitter(),
          },
        ],
        callback: (_index: number, _label: string) => {
          popover.dismiss();
          this.changeSubmitter();
        },
      },
      event,
      translucent: true,
    });
    await popover.present();
  }

  async changeSubmitter() {
    this.searchablePopoverService.allUsers({
      event,
      selectedUserId: this.conversation?.submitterUser?.id,
      dynamicCreationCallback: async (text: string) => {
        const submitterUser = await this.usersService.create(text);
        await this.updateSubmitter(submitterUser.id, submitterUser);
      },
      callback: async (id: string, name: string, submitterUser: any) => {
        await this.updateSubmitter(id, submitterUser);
      },
    });
  }

  private async updateSubmitter(id: string, submitterUser: any) {
    const loader = await this.loadingCtrl.create({
      message: 'Updating...',
      spinner: 'crescent',
    });
    loader.present();

    try {
      const updatedConversation = await this.conversationsService.update(this.conversation.id, {
        submitterUserId: id,
      });

      // Update the conversation's submitter user
      this.conversation.submitterUser = updatedConversation.submitterUser || submitterUser;

      // Reload the submitter profile to get all the custom fields
      await this.loadSubmitterProfile();

      loader.dismiss();
    } catch (e) {
      loader.dismiss();
      this.messageService.show('Failed to update, please try again');
      console.error(e);
    }
  }

  viewProfile() {
    if (this.conversation?.submitterUser?.id) {
      this.navController.navigateRoot(
        formatLink(this.conversation.submitterUser.id, this.conversation.submitterUser.isExternal),
      );
    }
  }

  async changeAccount(event: Event) {
    this.searchablePopoverService.accounts({
      event,
      selectedAccountId: this.conversation.accountId,
      callback: async (id: string) => {
        if (id) {
          const loader = await this.loadingCtrl.create({
            message: 'Updating...',
            spinner: 'crescent',
          });
          loader.present();

          try {
            const updatedConversation = await this.conversationsService.update(this.conversation.id, {
              accountId: id === this.conversation.accountId ? null : id,
            });
            // Only update the accountId since we don't want to overwrite other required fields
            this.conversation.accountId = updatedConversation.accountId;
            if (updatedConversation.account) {
              this.conversation.account = updatedConversation.account;
            }

            loader.dismiss();
          } catch (e) {
            loader.dismiss();
            this.messageService.show('Failed to update, please try again');
            console.error(e);
          }
        }
      },
    });
  }

  viewCustomer(customerId: string) {
    if (this.authService.tenant.accountsEnabled) {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.CHANNELS}/${customerId}/overview`);
    } else {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.CUSTOMERS}/${customerId}/overview`);
    }
  }

  viewAccount(accountId: string) {
    if (this.authService.tenant.accountsEnabled) {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.ACCOUNTS}?id=${accountId}`);
    } else {
      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.CUSTOMERS}/${accountId}/overview`);
    }
  }

  async loadSubmitterProfile() {
    if (!this.conversation?.submitterUser?.id) {
      return;
    }

    const profile: Profile = await this.authService.getProfileById(this.conversation.submitterUser.id);
    const customFields = [];

    if (profile.manager) {
      customFields.push({
        label: 'Manager',
        value: profile.manager.displayName,
      });
    }
    if (profile.title) {
      customFields.push({
        label: 'Title',
        value: profile.title,
      });
    }
    if (profile.department) {
      customFields.push({
        label: 'Department',
        value: profile.department,
      });
    }
    if (profile.locality) {
      customFields.push({
        label: 'Locality',
        value: profile.locality,
      });
    }
    if (profile.country) {
      customFields.push({
        label: 'Country',
        value: profile.country,
      });
    }
    if (profile.costCenter) {
      customFields.push({
        label: 'Cost center',
        value: profile.costCenter,
      });
    }
    if (profile.employeeType) {
      customFields.push({
        label: 'Employee type',
        value: profile.employeeType,
      });
    }
    if (profile.employeeNumber) {
      customFields.push({
        label: 'Employee #',
        value: profile.employeeNumber,
      });
    }

    this.customFields = [
      ...customFields,
      ...generateCustomFieldsArray(
        this.authService.customProfileFields,
        profile.customFields,
        profile.additionalIdpFields,
      ),
    ];

    this.conversation.submitterUser = {
      ...profile,
    };
  }

  accordionGroupChange = (event: CustomEvent) => {
    this.isInfoExpanded = event.detail.value.includes('info');

    if (this.isInfoExpanded) {
      this.loadSubmitterProfile();
    }
  };

  async editAccount(accountId: string) {
    const modal = await this.modalCtrl.create({
      component: EditAccountComponent,
      componentProps: {
        id: accountId,
      },
      cssClass: 'modal-medium',
    });

    await modal.present();

    modal.onDidDismiss().then(() => {
      // Refresh the conversation data if needed
      this.loadSubmitterProfile();
    });
  }
}
